import { useState } from 'react';
import { Num, T } from 'gt-react';
import { CaretDownIcon, CaretUpIcon, NoteIcon } from '@phosphor-icons/react';
import { AlertContainer, AlertDescription } from '@/components/ui/alert';
import MDBox from 'src/components/md/MDBox';
import MDTypography from 'src/components/md/MDTypography';
import type { EdiNote } from 'src/types/application/SalesOrderDraftDTO';

interface Props {
  /** Notes captured from the inbound 850, or null/undefined for non-EDI drafts. */
  ediNotes?: EdiNote[] | null;
}

/**
 * How much note text the collapsed banner shows: the first
 * {@link PREVIEW_LINE_COUNT} messages, each capped at
 * {@link PREVIEW_CHAR_LIMIT} characters. Long partner notes (Safeway's
 * supply-agreement boilerplate runs ~20 MSG segments) pushed the line items off
 * screen, so the collapsed banner stays a fixed two lines tall.
 */
const PREVIEW_LINE_COUNT = 2;
const PREVIEW_CHAR_LIMIT = 120;

/** One MSG segment of a note, numbered across the whole banner. */
interface NoteMessage {
  /** 1-based position in wire order, stable between collapsed and expanded. */
  number: number;
  text: string;
}

/** A note plus its heading and numbered messages, ready to render. */
interface PreparedNote {
  key: string;
  label: string | null;
  messages: NoteMessage[];
}

/** Matches an N903 description that says nothing beyond "this is a note". */
const BARE_NOTES_DESCRIPTION = /^notes?$/i;

/**
 * True when a note carries readable text rather than a bare reference code.
 *
 * A note whose only content is the description `Notes` is excluded: partners
 * commonly send that as N903, and it tells the user nothing the banner's own
 * title doesn't already say. Filtering it here rather than downstream keeps it
 * from resurfacing as a message body once {@link noteLabel} suppresses the
 * heading.
 */
function hasNoteText(note: EdiNote): boolean {
  const description = (note.description ?? '').trim();
  return (
    note.text.length > 0 ||
    (description.length > 0 && !BARE_NOTES_DESCRIPTION.test(description))
  );
}

/**
 * Stable React key for a note. EDI notes have no id, so the key is built from
 * the note's own content — which is what makes it stable across re-renders and
 * reorders, unlike an array index.
 */
function noteKey(note: EdiNote): string {
  return [
    note.qualifier,
    note.lineNumber ?? '',
    note.referenceId ?? '',
    note.description ?? '',
    ...note.text,
  ].join('|');
}

/**
 * The note's heading: N903 description, PO1 line, REF id — else the qualifier.
 *
 * Returns null when the description is a bare "Notes" and there's nothing else
 * to say: partners commonly send that as N903, which rendered as a second
 * "Notes" heading directly under the banner's own title.
 */
function noteLabel(note: EdiNote): string | null {
  const label = [
    note.description,
    note.lineNumber ? `Line ${note.lineNumber}` : undefined,
    note.referenceId,
  ]
    .filter(Boolean)
    .join(' · ');

  if (BARE_NOTES_DESCRIPTION.test(label.trim())) return null;
  return label || note.qualifier;
}

/**
 * Flatten the notes into headings plus numbered messages.
 *
 * Messages are numbered continuously across every note so the two lines in the
 * collapsed preview carry the same numbers they have in the expanded list — a
 * partner's "Message 2" is the same text in both places.
 *
 * Only MSG segments become messages. A note with none of them carries its whole
 * instruction in the heading, and {@link hasNoteText} guarantees that heading is
 * meaningful — so {@link previewLines} shows the label instead, rather than the
 * note being echoed as its own message body.
 */
function prepareNotes(notes: EdiNote[]): PreparedNote[] {
  let counter = 0;

  return notes.map((note) => ({
    key: noteKey(note),
    label: noteLabel(note),
    messages: note.text.flatMap((line) => {
      const text = line.trim();
      return text ? [{ number: ++counter, text }] : [];
    }),
  }));
}

/**
 * A token this long has no break opportunity a reader would recognize — a URL,
 * a GTIN, an encoded reference. Breaking mid-token is expected for those.
 */
const UNBREAKABLE_TOKEN_LENGTH = 40;

/**
 * How a message wraps. `overflow-wrap` (`wrap-anywhere` / `break-words`) is the
 * wrong tool here: it only breaks a word *to prevent overflow*, so the browser
 * still takes the ordinary break opportunity after "Message 5:" first and drops
 * the whole URL to the next line, leaving the label stranded. `break-all` adds
 * break opportunities between any two characters, so the token starts beside its
 * label and fills the line.
 *
 * It's applied per message rather than to the block: `break-all` on ordinary
 * prose splits real words at the line edge (`APPOINTMEN` / `T`), which is worse
 * than a ragged right margin.
 */
function wrapClass(text: string): string {
  return text
    .split(/\s+/)
    .some((token) => token.length > UNBREAKABLE_TOKEN_LENGTH)
    ? 'break-all'
    : 'break-words';
}

/**
 * The lines the collapsed banner shows.
 *
 * Normally these are the first messages, but a note can carry its whole
 * instruction in the N903 description with no MSG segment at all ("Do not break
 * pallets"). That text is the note's heading rather than a message, so it would
 * otherwise be absent from the preview — hiding the order's only instruction
 * behind the caret, which is exactly what this banner exists to prevent.
 */
function previewLines(
  notes: PreparedNote[],
): Array<{ key: string; number?: number; text: string }> {
  return notes
    .flatMap((note) =>
      note.messages.length > 0
        ? note.messages.map((message) => ({
            key: `${note.key}|${message.number}`,
            number: message.number,
            text: message.text,
          }))
        : note.label
          ? [{ key: note.key, text: note.label }]
          : [],
    )
    .slice(0, PREVIEW_LINE_COUNT);
}

/** Cap a single preview line so one enormous MSG segment can't dominate. */
function truncate(text: string): string {
  return text.length > PREVIEW_CHAR_LIMIT
    ? `${text.slice(0, PREVIEW_CHAR_LIMIT).trimEnd()}…`
    : text;
}

/** The message prefix — omitted when there's only one message to point at. */
function MessageNumber({ number }: { number: number }) {
  return (
    <span className="font-medium">
      <T context="prefix labelling one free-form message within the notes a trading partner sent on an EDI purchase order">
        Message <Num>{number}</Num>:
      </T>{' '}
    </span>
  );
}

/**
 * Surfaces the free-form N9/MSG notes a trading partner sent on the 850.
 *
 * These carry instructions our structured fields don't model — mixing-center
 * transfers, routing and appointment requirements. Until this banner existed the
 * text was only reachable by expanding "Show Raw EDI Data (Technical)" inside
 * the collapsed EDI history, so it was effectively invisible and orders were
 * processed against the wrong destination.
 *
 * Collapsed by default: rendering every note in full pushed the line items
 * below the fold on orders with long partner notes, so the banner stays the
 * title plus the first two messages (each truncated) until the user opens it.
 *
 * Reference-only N9 loops (an identifier with no message text) are filtered out
 * — they're routing codes, not instructions, and are already visible in the EDI
 * transaction viewer.
 */
export function EdiNotesBanner({ ediNotes }: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const notes = (ediNotes ?? []).filter(hasNoteText);
  if (notes.length === 0) return null;

  const prepared = prepareNotes(notes);
  const allMessages = prepared.flatMap((note) => note.messages);
  // A single message needs no "Message 1:" prefix — there's nothing to tell apart.
  const showNumbers = allMessages.length > 1;

  return (
    <AlertContainer className="mb-3 border-amber-200 bg-amber-50 py-2">
      <NoteIcon className="h-4 w-4 text-amber-700" />
      <AlertDescription className="ml-2 flex min-w-0 flex-col gap-1.5 text-sm text-amber-950">
        <button
          type="button"
          aria-expanded={isOpen}
          onClick={() => setIsOpen(!isOpen)}
          className="flex w-full min-w-0 flex-col items-start gap-0.5 text-left hover:opacity-80"
        >
          <span className="flex items-center gap-1.5 font-medium">
            {isOpen ? (
              <CaretUpIcon size={14} className="shrink-0" />
            ) : (
              <CaretDownIcon size={14} className="shrink-0" />
            )}
            <T context="header of the collapsible banner showing notes sent on an EDI purchase order, with the note count">
              Notes on this EDI order (<Num>{notes.length}</Num>)
            </T>
          </span>
          {!isOpen && (
            <span className="w-full min-w-0 pl-[calc(14px+0.375rem)] text-xs opacity-70">
              {previewLines(prepared).map((line) => (
                <span key={line.key} className="block truncate">
                  {showNumbers && line.number !== undefined && (
                    <MessageNumber number={line.number} />
                  )}
                  {truncate(line.text)}
                </span>
              ))}
            </span>
          )}
        </button>
        {isOpen && (
          <div className="flex max-h-48 min-w-0 flex-col gap-1.5 overflow-y-auto">
            {prepared.map((note) => (
              <div key={note.key} className="flex min-w-0 flex-col gap-0.5">
                {note.label && (
                  <span className="text-xs font-medium opacity-70">
                    {note.label}
                  </span>
                )}
                {note.messages.map((message) => (
                  <span
                    key={message.number}
                    className={`w-full min-w-0 whitespace-pre-wrap ${wrapClass(message.text)}`}
                  >
                    {showNumbers && <MessageNumber number={message.number} />}
                    {message.text}
                  </span>
                ))}
              </div>
            ))}
          </div>
        )}
      </AlertDescription>
    </AlertContainer>
  );
}

/**
 * The same notes as {@link EdiNotesBanner}, rendered as a titled card instead of
 * a collapsible alert — for the EDI transaction viewer, where it sits directly
 * under Party Information and matches that section's heading + grey card.
 *
 * Nothing is collapsed here, unlike the banner. The banner hides its body by
 * default because it renders directly above the line items, where a partner's
 * 20-segment note pushed them below the fold. This card lives in the right-hand
 * panel, which scrolls on its own, so the pressure that justified the toggle is
 * absent — and a second scroll area inside that panel is exactly what we removed
 * from this modal. Long notes simply make the panel longer.
 */
export function EdiNotesCard({ ediNotes }: Props) {
  const notes = (ediNotes ?? []).filter(hasNoteText);
  if (notes.length === 0) return null;

  const prepared = prepareNotes(notes);
  // A single message needs no "Message 1:" prefix — there's nothing to tell apart.
  const showNumbers = prepared.flatMap((note) => note.messages).length > 1;

  return (
    <>
      <MDTypography variant="subtitle2" fontWeight="bold" mb={1}>
        <T context="section title for the free-form notes a trading partner sent on an EDI purchase order, with the note count">
          Order Notes (<Num>{notes.length}</Num>)
        </T>
      </MDTypography>
      <MDBox
        sx={{
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          p: 2,
          mb: 2,
        }}
      >
        {prepared.map((note, index) => (
          <MDBox
            key={note.key}
            sx={{ mb: index === prepared.length - 1 ? 0 : 1.5 }}
          >
            {note.label && (
              <MDTypography
                variant="caption"
                fontWeight="bold"
                sx={{ display: 'block' }}
              >
                {note.label}
              </MDTypography>
            )}
            {note.messages.map((message) => (
              <MDTypography
                key={message.number}
                variant="caption"
                className={wrapClass(message.text)}
                sx={{ display: 'block', whiteSpace: 'pre-wrap' }}
              >
                {showNumbers && <MessageNumber number={message.number} />}
                {message.text}
              </MDTypography>
            ))}
          </MDBox>
        ))}
      </MDBox>
    </>
  );
}
